<template>
  <div class="mt-1">
    <b-row class="mb-1">
      <b-col cols="12" class="d-flex align-items-center">
        <b-button variant="outline-primary" class="btn-icon" :to="{ path: '/settings' }">
          <i class="fa-solid fa-arrow-left"></i>
          <span class="ml-25">Back</span>
        </b-button>
        <h3 class="ml-1 mb-0">Imports</h3>
      </b-col>
    </b-row>

    <!-- ── 1. CPD course catalogue ──────────────────────────────────── -->
    <b-card>
      <b-overlay :show="catalogueLoading" rounded="sm" no-fade>
        <h4>CPD Course Catalogue</h4>
        <p class="text-muted mb-1">
          Upload the e-learning course list to create or update CPD courses.
          Courses are matched on name, so re-uploading refreshes credits and
          hours rather than creating duplicates. Expected columns:
          <em>Course full name</em>, <em>Average time to complete in hours</em>,
          <em>CPD Credits</em>.
        </p>

        <b-button variant="primary" @click="$refs.catalogueInput.click()">
          Upload course list (.xlsx)
        </b-button>
        <input
          ref="catalogueInput"
          type="file"
          accept=".xlsx,.xls"
          class="d-none"
          @change="uploadCatalogue"
        />

        <b-alert
          v-if="catalogueResult"
          variant="success"
          show
          class="mt-1 mb-0"
        >
          <div class="alert-body">
            Created <strong>{{ catalogueResult.created }}</strong> and updated
            <strong>{{ catalogueResult.updated }}</strong> courses.
            <span v-if="catalogueResult.skipped.length">
              <strong>{{ catalogueResult.skipped.length }}</strong> row(s) skipped.
            </span>
          </div>
        </b-alert>

        <b-table
          v-if="catalogueResult && catalogueResult.skipped.length"
          small
          striped
          bordered
          responsive
          class="mt-1"
          :items="catalogueResult.skipped"
          :fields="['row', 'title', 'reason']"
        />
      </b-overlay>
    </b-card>

    <!-- ── 2. Completed courses ─────────────────────────────────────── -->
    <b-card>
      <b-overlay :show="completionsLoading" rounded="sm" no-fade>
        <h4>Completed Courses</h4>
        <p class="text-muted mb-1">
          Upload a regional completion export to record past e-learning as
          training events with attendance. Nothing is saved until you have
          reviewed and confirmed how each course, person and service area maps
          onto the system.
        </p>

        <!-- Step 1: upload -->
        <div v-if="!analysis && !commitResult">
          <b-button variant="primary" @click="$refs.completionsInput.click()">
            Upload completed courses (.xlsx)
          </b-button>
          <input
            ref="completionsInput"
            type="file"
            accept=".xlsx,.xls"
            class="d-none"
            @change="analyzeCompletions"
          />
        </div>

        <!-- Step 2: review -->
        <import-review
          v-if="analysis && !commitResult"
          :analysis="analysis"
          :course-options="courseOptions"
          :user-options="userOptions"
          @cancel="resetCompletions"
          @confirm="commitCompletions"
        />

        <!-- Step 3: result -->
        <div v-if="commitResult">
          <b-alert variant="success" show class="mb-1">
            <div class="alert-body">
              Import complete —
              <strong>{{ commitResult.events_created }}</strong> events created,
              <strong>{{ commitResult.events_reused }}</strong> reused,
              <strong>{{ commitResult.users_created }}</strong> users created,
              <strong>{{ commitResult.attendance }}</strong> attendance records written.
            </div>
          </b-alert>

          <b-table
            v-if="commitResult.skipped && commitResult.skipped.length"
            small
            striped
            bordered
            responsive
            class="mb-1"
            :items="commitResult.skipped"
            :fields="['row', 'reason']"
          />

          <b-button variant="outline-primary" @click="resetCompletions">
            Import another file
          </b-button>
        </div>
      </b-overlay>
    </b-card>

    <!-- ── 3. Remembered mappings ───────────────────────────────────── -->
    <b-card>
      <b-overlay :show="mappingsLoading" rounded="sm" no-fade>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h4 class="mb-0">Remembered Mappings</h4>
          <b-button size="sm" variant="outline-primary" @click="loadMappings">
            Refresh
          </b-button>
        </div>
        <p class="text-muted">
          Choices you confirmed on previous imports. Remove one to be asked
          about it again next time.
        </p>

        <h6 class="mt-1">Service area → sites</h6>
        <b-table
          small
          striped
          bordered
          responsive
          :items="mappings.service_areas"
          :fields="serviceAreaFields"
          show-empty
          empty-text="No service area mappings saved yet"
        >
          <template #cell(sites)="{ item }">
            <b-badge
              v-for="s in item.sites"
              :key="s.id"
              variant="light-primary"
              class="mr-25"
            >
              {{ s.title }}
            </b-badge>
          </template>
          <template #cell(actions)="{ item }">
            <b-button size="sm" variant="outline-danger" @click="removeServiceArea(item)">
              Remove
            </b-button>
          </template>
        </b-table>

        <h6 class="mt-2">Spreadsheet course name → course</h6>
        <b-table
          small
          striped
          bordered
          responsive
          :items="mappings.course_aliases"
          :fields="courseAliasFields"
          show-empty
          empty-text="No course mappings saved yet"
          :per-page="10"
          :current-page="aliasPage"
        >
          <template #cell(actions)="{ item }">
            <b-button size="sm" variant="outline-danger" @click="removeCourseAlias(item)">
              Remove
            </b-button>
          </template>
        </b-table>
        <b-pagination
          v-if="mappings.course_aliases.length > 10"
          v-model="aliasPage"
          :total-rows="mappings.course_aliases.length"
          :per-page="10"
          size="sm"
          class="mb-0"
        />
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BAlert,
  BTable,
  BBadge,
  BPagination,
} from "bootstrap-vue";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import ImportReview from "./ImportReview.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BAlert,
    BTable,
    BBadge,
    BPagination,
    ImportReview,
    ToastificationContent,
  },
  setup() {
    const MODULE = "settings";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, settingsStoreModule);
    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
    });
  },
  data() {
    return {
      catalogueLoading: false,
      catalogueResult: null,

      completionsLoading: false,
      analysis: null,
      commitResult: null,
      courseOptions: [],
      userOptions: [],

      mappingsLoading: false,
      mappings: { service_areas: [], course_aliases: [] },
      aliasPage: 1,
      serviceAreaFields: [
        { key: "company", label: "Region" },
        { key: "service_area", label: "Service area" },
        { key: "sites", label: "Sites" },
        { key: "actions", label: "", class: "text-center" },
      ],
      courseAliasFields: [
        { key: "source_value", label: "Spreadsheet name" },
        { key: "target", label: "Course" },
        { key: "actions", label: "", class: "text-center" },
      ],
    };
  },
  created() {
    this.loadMappings();
  },
  methods: {
    toast(title, variant = "danger", icon = "AlertTriangleIcon") {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      });
    },

    // The API returns { error } with a 4xx for bad files; surface that text.
    apiError(error, fallback) {
      const msg =
        (error && error.response && error.response.data && error.response.data.error) ||
        fallback;
      this.toast(msg);
    },

    uploadCatalogue(event) {
      const file = event.target.files[0];
      // Reset so re-selecting the same file fires change again.
      this.$refs.catalogueInput.value = "";
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      this.catalogueLoading = true;
      this.catalogueResult = null;
      store
        .dispatch("settings/importCpdCourses", formData)
        .then((res) => {
          this.catalogueResult = res.data;
          this.toast("Course catalogue imported", "success", "ThumbsUpIcon");
        })
        .catch((e) => this.apiError(e, "Course catalogue import failed"))
        .finally(() => {
          this.catalogueLoading = false;
        });
    },

    analyzeCompletions(event) {
      const file = event.target.files[0];
      this.$refs.completionsInput.value = "";
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      this.completionsLoading = true;
      store
        .dispatch("settings/analyzeCpdCompletions", formData)
        .then((res) => {
          this.analysis = res.data;
          return this.loadReviewOptions();
        })
        .catch((e) => this.apiError(e, "Could not read that spreadsheet"))
        .finally(() => {
          this.completionsLoading = false;
        });
    },

    // The review dropdowns need every course and every user in the region,
    // not just the ones the matcher suggested.
    loadReviewOptions() {
      return Promise.all([
        store.dispatch("settings/fetchCourses", { per_page: 1000 }),
        store.dispatch("settings/fetchUsers", {
          company_id: this.analysis.company.id,
          per_page: 1000,
        }),
      ])
        .then(([courses, users]) => {
          const courseData = courses.data.data || courses.data;
          this.courseOptions = (courseData || []).map((c) => ({
            id: c.id,
            title: c.title,
          }));

          const userData = users.data.data || users.data;
          this.userOptions = (userData || []).map((u) => ({
            id: u.id,
            label: `${u.name} ${u.surname}${u.email ? " · " + u.email : ""}`,
          }));
        })
        .catch(() => this.toast("Could not load the course and user lists"));
    },

    commitCompletions(payload) {
      this.completionsLoading = true;
      store
        .dispatch("settings/commitCpdCompletions", payload)
        .then((res) => {
          this.commitResult = res.data;
          this.analysis = null;
          this.toast("Completed courses imported", "success", "ThumbsUpIcon");
          this.loadMappings();
        })
        .catch((e) => this.apiError(e, "Import failed"))
        .finally(() => {
          this.completionsLoading = false;
        });
    },

    resetCompletions() {
      this.analysis = null;
      this.commitResult = null;
    },

    loadMappings() {
      this.mappingsLoading = true;
      store
        .dispatch("settings/fetchCpdMappings")
        .then((res) => {
          this.mappings = res.data;
        })
        .catch(() => this.toast("Could not load saved mappings"))
        .finally(() => {
          this.mappingsLoading = false;
        });
    },

    removeServiceArea(item) {
      store
        .dispatch("settings/deleteServiceAreaMapping", {
          company_id: item.company_id,
          service_area: item.service_area,
        })
        .then(() => this.loadMappings())
        .catch(() => this.toast("Could not remove that mapping"));
    },

    removeCourseAlias(item) {
      store
        .dispatch("settings/deleteCourseAlias", { id: item.id })
        .then(() => this.loadMappings())
        .catch(() => this.toast("Could not remove that mapping"));
    },
  },
};
</script>
